'use client'
import { type PropsWithChildren, createContext, useContext } from 'react';

import { useAccessTokenBySlugQuery } from 'reservations/account/queries/useAccessTokenBySlugQuery';
import type { GetAccessTokenBySlug } from 'reservations/account/queries/useAccessTokenBySlugQuery';
import { ErrorScreenWithAccountDetails } from 'reservations/base/components/ErrorScreen';

interface AccessTokenContextInterface extends GetAccessTokenBySlug {
  slug: string;
}

const AccessTokenContext = createContext<AccessTokenContextInterface | null>(
  null
);

type AccessTokenProviderProps = {
  slug: string;
};

export function AccessTokenProvider({
  slug,
  children
}: PropsWithChildren<AccessTokenProviderProps>) {
  const {
    data = {} as AccessTokenContextInterface,
    isPending,
    isError
  } = useAccessTokenBySlugQuery(slug);

  if (isPending) {
    return <></>;
  }

  if (isError) {
    return <ErrorScreenWithAccountDetails />;
  }

  return (
    <AccessTokenContext.Provider value={{ ...data, slug }}>
      {children}
    </AccessTokenContext.Provider>
  );
}

export function useAccessToken() {
  const context = useContext(AccessTokenContext);
  if (context == null) {
    throw new Error('useAccessToken must be used within AccessTokenProvider');
  }

  return {
    ...context,
    apiKey: context.access_token,
    accessToken: context.access_token
  };
}
